import api from './api';
import { getApiUrl } from './config';
import { handleApiCall } from '../utils/apiResponseHandler';
import { endpoint } from './endpoints';

export interface TrialUser {
  id: string;
  name: string;
  email: string;
  accountStatus: string;
  trialType: 'basic' | 'full_fledged';
  trialStartDate: string;
  trialEndDate: string;
  daysRemaining: number;
  isTrialActive: boolean;
  userType: 'agent' | 'agency_owner';
  agency?: {
    id: string;
    name: string;
  };
  lastLoginAt?: string;
  createdAt: string;
  npn?: string;
  contactPhone?: string;
}

export interface TrialStats {
  totalTrials: number;
  activeTrials: number;
  expiredTrials: number;
  convertedTrials: number;
  thisWeek: number;
  thisMonth: number;
}

export interface TrialFilters {
  status?: 'trial_active' | 'trial_expired_locked' | 'active';
  trialType?: 'basic' | 'full_fledged';
  search?: string;
  page?: number;
  limit?: number;
}

export interface TrialResponse {
  users: TrialUser[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

class TrialManagementService {
  /**
   * Get all trial users with filtering and pagination
   */
  async getTrialUsers(filters: TrialFilters = {}): Promise<TrialResponse> {
    try {
      const params = new URLSearchParams();
      
      if (filters.status) params.append('status', filters.status);
      if (filters.trialType) params.append('trialType', filters.trialType);
      if (filters.search) params.append('search', filters.search);
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());

      const result = await handleApiCall<TrialResponse>(async () => {
        const response = await api.get({ 
          url: getApiUrl(endpoint.admin.trialUsers.list + (params.toString() ? `?${params.toString()}` : '')),
          auth: true // Add authentication
        });
        return response;
      });
      
      return result;
    } catch (error) {

      throw new Error('Failed to fetch trial users');
    }
  }

  /**
   * Get trial statistics
   */
  async getTrialStats(): Promise<TrialStats> {
    try {
      const result = await handleApiCall<TrialStats>(async () => {
        const response = await api.get({ 
          url: getApiUrl(endpoint.admin.trialUsers.stats),
          auth: true // Add authentication
        });
        return response;
      });
      
      return result;
    } catch (error) {

      throw new Error('Failed to fetch trial statistics');
    }
  }

  /**
   * Get a specific trial user by ID
   */
  async getTrialUser(userId: string): Promise<TrialUser> {
    try {
      const result = await handleApiCall<TrialUser>(async () => {
        const response = await api.get({ 
          url: getApiUrl(endpoint.admin.trialUsers.get(userId)),
          auth: true // Add authentication
        });
        return response;
      });
      
      return result;
    } catch (error) {

      throw new Error('Failed to fetch trial user');
    }
  }

  /**
   * Extend a user's trial
   */
  async extendTrial(userId: string, days: number, adminNotes?: string): Promise<TrialUser> {
    try {
      const result = await handleApiCall<TrialUser>(async () => {
        const response = await api.put({ 
          url: getApiUrl(endpoint.admin.trialUsers.extend(userId)), 
          body: { 
            days, 
            adminNotes,
            sendEmail: true // Backend will send extension email via SendGrid
          },
          auth: true // Add authentication
        });
        return response;
      });

      return result;
    } catch (error) {

      throw new Error('Failed to extend trial');
    }
  }

  /**
   * Convert trial to paid subscription
   */
  async convertTrial(userId: string, planId: string, adminNotes?: string): Promise<TrialUser> {
    try {
      const result = await handleApiCall<TrialUser>(async () => {
        const response = await api.put({ 
          url: getApiUrl(endpoint.admin.trialUsers.convert(userId)), 
          body: { planId, adminNotes },
          auth: true // Add authentication
        });
        return response;
      });
      
      return result;
    } catch (error) {

      throw new Error('Failed to convert trial');
    }
  }

  /**
   * Suspend a trial user
   */
  async suspendTrialUser(userId: string, reason: string, adminNotes?: string): Promise<TrialUser> {
    try {
      const result = await handleApiCall<TrialUser>(async () => {
        const response = await api.put({ 
          url: getApiUrl(endpoint.admin.trialUsers.suspend(userId)), 
          body: { reason, adminNotes },
          auth: true // Add authentication
        });
        return response;
      });
      
      return result;
    } catch (error) {

      throw new Error('Failed to suspend trial user');
    }
  }

  /**
   * Reactivate a suspended trial user
   */
  async reactivateTrialUser(userId: string, adminNotes?: string): Promise<TrialUser> {
    try {
      const result = await handleApiCall<TrialUser>(async () => {
        const response = await api.put({ 
          url: getApiUrl(endpoint.admin.trialUsers.reactivate(userId)), 
          body: { adminNotes },
          auth: true // Add authentication
        });
        return response;
      });
      
      return result;
    } catch (error) {

      throw new Error('Failed to reactivate trial user');
    }
  }

  /**
   * Get trial usage statistics for a user
   */
  async getTrialUsage(userId: string): Promise<{
    leadsCreated: number;
    callsMade: number;
    smsSent: number;
    appointmentsScheduled: number;
    documentsUploaded: number;
    lastActivity: string;
  }> {
    try {
      const result = await handleApiCall<{
        leadsCreated: number;
        callsMade: number;
        smsSent: number;
        appointmentsScheduled: number;
        documentsUploaded: number;
        lastActivity: string;
      }>(async () => {
        const response = await api.get({ 
          url: getApiUrl(endpoint.admin.trialUsers.usage(userId)),
          auth: true // Add authentication
        });
        return response;
      });
      
      return result;
    } catch (error) {

      throw new Error('Failed to fetch trial usage');
    }
  }

  /**
   * Get trial conversion analytics
   */
  async getTrialConversionAnalytics(dateFrom?: string, dateTo?: string): Promise<{
    conversionRate: number;
    averageTrialDuration: number;
    topConvertingPlans: Array<{ planName: string; conversions: number }>;
    trialAbandonmentReasons: Array<{ reason: string; count: number }>;
  }> {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('dateFrom', dateFrom);
      if (dateTo) params.append('dateTo', dateTo);

      const result = await handleApiCall<{
        conversionRate: number;
        averageTrialDuration: number;
        topConvertingPlans: Array<{ planName: string; conversions: number }>;
        trialAbandonmentReasons: Array<{ reason: string; count: number }>;
      }>(async () => {
        const response = await api.get({ 
          url: getApiUrl(endpoint.admin.trialUsers.analytics + (params.toString() ? `?${params.toString()}` : '')),
          auth: true // Add authentication
        });
        return response;
      });
      
      return result;
    } catch (error) {

      throw new Error('Failed to fetch trial conversion analytics');
    }
  }

  /**
   * Send trial expiration reminder
   * Backend handles SendGrid email sending automatically
   */
  async sendTrialExpirationReminder(userId: string, daysUntilExpiration: number): Promise<{ success: boolean }> {
    try {
      const result = await handleApiCall<{ success: boolean }>(async () => {
        const response = await api.post<{
          statusCode: number;
          message: string;
          data: { success: boolean };
          timestamp: string;
        }>({ 
          url: getApiUrl(endpoint.admin.trialUsers.sendReminder(userId)), 
          body: { 
            daysUntilExpiration,
            sendEmail: true // Backend will send reminder email via SendGrid
          } 
        });
        return response;
      });
      
      return result;
    } catch (error) {

      throw new Error('Failed to send trial expiration reminder');
    }
  }

  /**
   * Bulk extend trials
   */
  async bulkExtendTrials(userIds: string[], days: number, adminNotes?: string): Promise<{ success: number; failed: number }> {
    try {
      const result = await handleApiCall<{ success: number; failed: number }>(async () => {
        const response = await api.post<{
          statusCode: number;
          message: string;
          data: { success: number; failed: number };
          timestamp: string;
        }>({ 
          url: getApiUrl(endpoint.admin.trialUsers.bulkExtend), 
          body: { userIds, days, adminNotes } 
        });
        return response;
      });
      
      return result;
    } catch (error) {

      throw new Error('Failed to bulk extend trials');
    }
  }
}

export const trialManagementService = new TrialManagementService();
